import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DataChange } from 'devextreme/common/grids';
import Tabs, { Item } from 'devextreme-react/tabs';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { Toast } from 'devextreme-react/toast';
import { formatMessage } from 'devextreme/localization';

import { Button, TextBox } from 'devextreme-react';

import StringHelper from '../../tools/StringHelper';

import MessageForm, { MessageFormHandle, ErrorMessage } from '../../components/message-form/MessageForm';
import PathApi from '../../api/PathApi';
import PathVersionStatus from '../../components/path-version-status/PathVersionStatus';
import ChangeInProgressForm from '../../components/change-in-progress-form/ChangeInProgressForm';

import SourceSelection from '../../components/source-selection/SourceSelection';
import FiltersSelection from '../../components/filters-selection/FiltersSelection';
import useFilteringCapabilities from '../../components/filters-selection/useFilteringCapabilities';
import { collectDeclaredParameters } from '../../components/filter-selection/FilterBuilderInline';
import MetaApiExposedFields, { MetaApiExposedFieldsHandle } from '../../components/metaapi-exposed-fields/MetaApiExposedFields';
import MetaApiResume from '../../components/metaapi-resume/MetaApiResume';

import PathMetaApiApi from '../../api/PathMetaApiApi';

import GetPathMetaApiDetailResult from '../../classes/api/result/pathmetaapimodule/GetPathMetaApiDetailResult';
import PathMetaApiMappingDto from '../../classes/dtos/pathmetaapimodule/PathMetaApiMappingDto';

import DataTableReadDto from '../../classes/dtos/pathcommonmodule/DataTableReadDto';
import DataSourceDataTableReadDto from '../../classes/dtos/pathcommonmodule/DataSourceDataTableReadDto';
import PathDataTableReadDto from '../../classes/dtos/pathcommonmodule/PathDataTableReadDto';

import MetaApiFieldMappingDto from '../../classes/dtos/pathmetaapimodule/MetaApiFieldMappingDto';

import PathCheckResultDto from '../../classes/dtos/pathmodule/PathCheckResultDto';
import { checkPathBeforeSaveAndPublish, getPathPublishChecklist, PathBeforeSaveValidationInput, ValidationRow } from '../../logics/pathmodule/PathBeforeSaveValidation';
import { performPathSave } from '../../logics/pathmodule/performPathSave';
import { useAuth } from '../../contexts/auth';

import TransformationCapabilitiesDto from '../../classes/dtos/transformationmodule/TransformationCapabilitiesDto';
import TransformationNotConfigurableDto from '../../classes/dtos/transformationmodule/TransformationNotConfigurableDto';
import PathMetaApiMappingCapabilitiesDto from '../../classes/dtos/pathmetaapimodule/PathMetaApiMappingCapabilitiesDto';

import DataTypeDto from '../../classes/dtos/datastructuremodule/DataTypeDto';

import FieldResumeDto from '../../classes/dtos/pathcommonmodule/FieldResumeDto';


export default function PathMetaApi() {

    const { id } = useParams();


    const { sessionDetail } = useAuth();
    const navigate = useNavigate();

    const [searchParams, setSearchParams] = useSearchParams();

    const pathId = Number.parseInt(id ?? "0");

    // Feature flag BD (Feature.FilterVisibility) : première condition d'affichage de l'onglet "Filters".
    const filterTabsEnabled = sessionDetail?.WithFilterVisibility ?? false;

    const messageFormHandleRef = useRef<MessageFormHandle>(null);

    const metaApiExposedFieldsRef = useRef<MetaApiExposedFieldsHandle>(null);

    const [pathMetaApiDetail, setPathMetaApiDetail] = useState<GetPathMetaApiDetailResult | null>(null);

    // "Advanced mode" checkbox (FILTER column header). Page-level state, persisted on the mapping version.
    const [advancedFilterMode, setAdvancedFilterMode] = useState<boolean>(false);
    // Advanced mode is a one-way switch: once it has been saved it can't be turned back off.
    const [advancedFilterModeLocked, setAdvancedFilterModeLocked] = useState<boolean>(false);

    // L'onglet "Filters" (mode complexe) n'est disponible que si la feature BD est active ET que le mode avancé
    // est activé sur ce path — fusion du feature flag Feature.FilterVisibility et de l'ancien `!simpleFilterMode`.
    const filtersTabAvailable = filterTabsEnabled && advancedFilterMode;

    // Colonne FILTER des grilles : visible seulement si la table source gère le filtrage. Même calcul
    // (`withFilter`) que l'onglet "Filters" dans FiltersSelection, via le hook partagé.
    const filterSourceTableReferenceId = pathMetaApiDetail?.Source instanceof DataSourceDataTableReadDto ? (pathMetaApiDetail.Source.TableReferenceId ?? 0) : 0;
    const { withFilter: sourceHandlesFilters } = useFilteringCapabilities(filterSourceTableReferenceId);

    const [capabilities, setCapabilities] = useState<PathMetaApiMappingCapabilitiesDto[]>([]);
    const [transformationsCapabilities, setTransformationsCapabilities] = useState<TransformationCapabilitiesDto[]>([]);

    const [mapping, setMapping] = useState<MetaApiFieldMappingDto[]>([]);

    const MAX_TAB = 3;
    const FILTERS_TAB = 2;

    const [currentTab, setCurrentTab] = useState<number>(() => {
        const tabFromUrl = Number(searchParams.get('tab'));
        const isValid = !Number.isNaN(tabFromUrl) && tabFromUrl >= 0 && tabFromUrl <= MAX_TAB && (filterTabsEnabled || tabFromUrl !== FILTERS_TAB);
        return isValid ? tabFromUrl : 0;
    });

    const [isOpen, setIsOpen] = useState<boolean>(false);

    const changeInProgress = useRef<boolean>(false);

    // Mirror of changeInProgress for rendering (the ref alone doesn't trigger a re-render).
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
    const [isPublished, setIsPublished] = useState<boolean>(false);

    const markChanged = useCallback(() => {
        changeInProgress.current = true;
        setHasUnsavedChanges(true);
    }, []);

    const [editNameInprogress, setEditNameInprogress] = useState<string | null>(null);

    const dataChangesRef = useRef<DataChange[]>([]);

    const selectedIdsRef = useRef<number[]>([]);

    const pathMetaApiMappingDtosRef = useRef<PathMetaApiMappingDto[]>([]);

    const [toastConfig, setToastConfig] = useState({
        isVisible: false,
        type: 'info',
        message: '',
        withRedirect: false
    } as {
        isVisible: boolean,
        type: 'info' | 'error' | 'success',
        message: string,
        withRedirect: boolean
    });

    useEffect(() => {
        PathMetaApiApi.GetPathMetaApiDetailAsync(pathId)
            .then(detail => {
                if (detail.IsFailed()) {
                    navigate("/paths");
                }
                else {
                    setPathMetaApiDetail(detail.Result as GetPathMetaApiDetailResult);
                    setAdvancedFilterMode((detail.Result as GetPathMetaApiDetailResult).AdvancedFilterMode);
                    setIsPublished((detail.Result as GetPathMetaApiDetailResult).PublishedVersionId !== null && (detail.Result as GetPathMetaApiDetailResult).PublishedVersionId === (detail.Result as GetPathMetaApiDetailResult).LastVersionId);
                    setAdvancedFilterModeLocked((detail.Result as GetPathMetaApiDetailResult).AdvancedFilterMode);
                }
            });
    }, [pathId, navigate]);

    const dataLoad = useCallback(async () => {

        if (pathMetaApiDetail === null) {
            return;
        }

        if (pathMetaApiDetail.Source === null) {
            return;
        }

        let tableReferenceId: number | null = null;
        let pathId: number | null = null;

        if (pathMetaApiDetail.Source instanceof DataSourceDataTableReadDto) {
            if ((pathMetaApiDetail.Source.DataSourceId ?? 0) > 0 && (pathMetaApiDetail.Source.TableReferenceId ?? 0) > 0) {
                tableReferenceId = pathMetaApiDetail.Source.TableReferenceId;
            }
        }

        if (pathMetaApiDetail.Source instanceof PathDataTableReadDto) {
            if ((pathMetaApiDetail.Source.PathId ?? 0) > 0) {
                pathId = pathMetaApiDetail.Source.PathId;
            }
        }

        if (tableReferenceId !== null || pathId !== null) {

            let getPathMetaApiCapabilitiesResult = await PathMetaApiApi.GetPathMetaApiCapabilitiesAsync(tableReferenceId, pathId);

            if (getPathMetaApiCapabilitiesResult.IsFailed()) {
                alert("Error : " + getPathMetaApiCapabilitiesResult.ErrorMessage);
                return;
            }

            let allFieldsSource = getPathMetaApiCapabilitiesResult.Result.SourceFields
                .toSorted((a, b) => (a.Name.toLowerCase() > b.Name.toLowerCase()) ? 1 : (a.Name.toLowerCase() < b.Name.toLowerCase()) ? -1 : 0);

            setCapabilities(getPathMetaApiCapabilitiesResult.Result.MappingsCapabilities);
            let transformationsCapabilitiesTemp = getPathMetaApiCapabilitiesResult.Result.TransformationsCapabilities;
            transformationsCapabilitiesTemp.push(new TransformationNotConfigurableDto(0, "Direct", 0, new DataTypeDto(0, false, ""), new DataTypeDto(0, false, ""), "TransformationNotConfigurable"));
            setTransformationsCapabilities(transformationsCapabilitiesTemp);

            let Mappings = pathMetaApiMappingDtosRef.current.length > 0
                ? pathMetaApiMappingDtosRef.current.map(e => new PathMetaApiMappingDto(e.FieldReferenceId, e.TransformationLogicId, e.TransformationParameters, e.TechnicalName, selectedIdsRef.current.includes(e.FieldReferenceId), e.Filters, e.DeclaredVariables))
                : (pathMetaApiDetail?.Mappings ?? []);

            let mappingTemp = allFieldsSource.map((e) => {
                let readMapping = Mappings?.find(x => x.FieldReferenceId === e.Id) ?? null;
                if (readMapping === null) {
                    return new MetaApiFieldMappingDto(e.Id, null, null, e.Name, e.Type, StringHelper.ToLabelForApi(e.Name), []);
                } else {
                    return new MetaApiFieldMappingDto(e.Id, readMapping.TransformationLogicId, readMapping.TransformationParameters, e.Name, e.Type, readMapping.TechnicalName, readMapping.Filters, readMapping.DeclaredVariables);
                }
            })

            selectedIdsRef.current = Mappings.filter(e => e.IsSelected).map(e => e.FieldReferenceId);

            setMapping(mappingTemp);

            let result = new Array<PathMetaApiMappingDto>();

            mappingTemp.forEach((e: MetaApiFieldMappingDto) => {
                let readMapping = Mappings.find(x => x.FieldReferenceId === e.SourceFieldReferenceId) ?? null;
                result.push(new PathMetaApiMappingDto(e.SourceFieldReferenceId ?? 0, e.TransformationType ?? 0, e.TransformationParameters ?? "", e.TechnicalName, readMapping?.IsSelected ?? false, e.Filters, e.DeclaredVariables));
            });

            pathMetaApiMappingDtosRef.current = result;
        } else {
            setCapabilities([]);
            setTransformationsCapabilities([]);
            setMapping([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathMetaApiDetail?.Source]);

    useEffect(() => {
        dataLoad()
    }, [dataLoad]);

    // The name is saved on its own (not part of the version): it never creates a version nor counts as an unsaved change.
    async function handleNameChange() {
        const newName = editNameInprogress;
        if (newName === null) {
            return;
        }

        const renameResult = await PathApi.RenamePathAsync(pathId, newName);
        if (renameResult.IsFailed()) {
            messageFormHandleRef.current?.Open(formatMessage("PathValidation_SaveFailed"), ErrorMessage);
            return;
        }

        setPathMetaApiDetail((previous) => {
            if (previous === null) {
                return previous;
            }
            previous.Name = newName;
            return previous;
        });
        setEditNameInprogress(null);
        setToastConfig({
            ...toastConfig,
            isVisible: true,
            type: 'success',
            message: formatMessage('PathName_Saved'),
            withRedirect: false
        });
    }

    const handleDataChanges = useCallback((changes: DataChange[], origin: MetaApiFieldMappingDto[]) => {
        dataChangesRef.current = changes;

        // Grid edits are unsaved changes too (the save buttons depend on it).
        if (changes.length > 0) {
            markChanged();
        }

        let result = new Array<PathMetaApiMappingDto>();

        origin.forEach((e: MetaApiFieldMappingDto) => {
            let existing = pathMetaApiMappingDtosRef.current.find(x => x.FieldReferenceId === e.SourceFieldReferenceId) ?? null;
            result.push(new PathMetaApiMappingDto(e.SourceFieldReferenceId, e.TransformationType ?? 0, e.TransformationParameters ?? "", e.TechnicalName, existing?.IsSelected ?? false, e.Filters, e.DeclaredVariables));
        });

        changes.forEach((e: DataChange) => {

            let row = result.find((x) => x.FieldReferenceId === e.key) ?? null;

            if (row !== null) {
                if (e.data.SourceFieldReferenceId !== undefined) {
                    row.FieldReferenceId = e.data.SourceFieldReferenceId;
                }
                if (e.data.TransformationType !== undefined) {
                    row.TransformationLogicId = e.data.TransformationType ?? 0;
                }
                if (e.data.TransformationParameters !== undefined) {
                    row.TransformationParameters = e.data.TransformationParameters;
                }
                if (e.data.TechnicalName !== undefined) {
                    row.TechnicalName = e.data.TechnicalName;
                }
                if (e.data.DeclaredVariables !== undefined) {
                    row.DeclaredVariables = e.data.DeclaredVariables;
                }
                if (e.data.Filters !== undefined) {
                    row.Filters = e.data.Filters;
                }
            }
        });

        pathMetaApiMappingDtosRef.current = result;

    }, []);

    const handleSelectionChanges = useCallback((ids: number[]) => {
        selectedIdsRef.current = ids;
        markChanged();
    }, []);

    const handleAdvancedFilterModeChange = useCallback((value: boolean) => {
        if (advancedFilterModeLocked && !value) {
            return;
        }
        setAdvancedFilterMode(value);
        markChanged();
    }, [advancedFilterModeLocked]);

    const handleDataSourceRefreshed = useCallback(() => {
        dataLoad();
    }, [dataLoad]);

    const handleTableMappingChange = useCallback((tableMappingReadDto: DataTableReadDto) => {
        setPathMetaApiDetail((previous) => {
            if (previous === null) {
                return previous;
            }

            markChanged();

            return new GetPathMetaApiDetailResult(
                previous.Id,
                previous.Name,
                tableMappingReadDto,
                previous.Mappings,
                previous.Schedule,
                previous.LastVersionId,
                previous.PublishedVersionId,
                previous.AdvancedFilterMode);
        });

    }, []);

    function GetPathMetaApiMappingCapabilities(fieldId: number) {
        return capabilities.find((e) => e.SourceFieldId === fieldId) ?? null;
    }

    function GetFieldName(fieldId: number) {
        return mapping.find((e) => e.SourceFieldReferenceId === fieldId)?.Name ?? "";
    }

    function buildPathValidationInput(): PathBeforeSaveValidationInput {
        const selectedRows: ValidationRow[] = pathMetaApiMappingDtosRef.current
            .filter((e) => selectedIdsRef.current.includes(e.FieldReferenceId))
            .map((e) => ({
                labelFieldId: e.FieldReferenceId,
                transformationLogicId: e.TransformationLogicId,
                transformationParameters: e.TransformationParameters,
                fieldName: e.TechnicalName,
            }));

        return {
            name: pathMetaApiDetail?.Name,
            source: pathMetaApiDetail?.Source,
            selectedRows,
            fieldNameMode: 'apiLabel',
            transformationsCapabilities,
            getAllowedTransformationLogicIds: (row) =>
                GetPathMetaApiMappingCapabilities(row.labelFieldId)?.TransformationLogicIds ?? null,
            getFieldLabel: (fieldId) => GetFieldName(fieldId),
        };
    }

    function CheckBeforeSaveAndPublish(): PathCheckResultDto {
        return checkPathBeforeSaveAndPublish(buildPathValidationInput());
    }

    async function save(publish: boolean): Promise<boolean> {
        const detail = pathMetaApiDetail;
        if (detail === null || detail.Source === null || messageFormHandleRef.current === null) {
            messageFormHandleRef.current?.Open(formatMessage("PathValidation_TechnicalError"), ErrorMessage);
            return false;
        }
        const source = detail.Source;

        const allMappings = pathMetaApiMappingDtosRef.current.map(e => new PathMetaApiMappingDto(e.FieldReferenceId, e.TransformationLogicId, e.TransformationParameters, e.TechnicalName, selectedIdsRef.current.includes(e.FieldReferenceId), e.Filters, e.DeclaredVariables));

        return performPathSave({
            messageForm: messageFormHandleRef.current,
            checkResult: CheckBeforeSaveAndPublish(),
            checklist: getPathPublishChecklist(buildPathValidationInput()),
            publish,
            persist: () => PathMetaApiApi.UpdatePathMetaApiDetailAsync(pathId, detail.Name, allMappings, source, detail.Schedule, advancedFilterMode, publish),
            onSuccess: (isPublished) => {
                metaApiExposedFieldsRef.current?.Save();
                if (advancedFilterMode) {
                    setAdvancedFilterModeLocked(true);
                }
                changeInProgress.current = false;
                setHasUnsavedChanges(false);
                setIsPublished(isPublished);
                setToastConfig({
                    ...toastConfig,
                    isVisible: true,
                    type: 'success',
                    message: publish ? 'Path saved and published' : 'Path saved as draft',
                    withRedirect: false
                });
            },
        });
    }

    const onHiding = useCallback(() => {
        setToastConfig({
            ...toastConfig,
            isVisible: false,
        });

        if (toastConfig.withRedirect) {
            navigate("/paths");
        }

    }, [toastConfig, navigate]);

    function cancel(): void {

        if (changeInProgress.current) {
            setIsOpen(true);
        }
        else {
            navigate("/paths");
        }
    }

    function cancelFromModal(): void {

        setIsOpen(false);
        navigate("/paths");
    }

    async function saveDraftFromModal(): Promise<void> {

        setIsOpen(false);
        if (await save(false)) {
            navigate("/paths");
        }
    }


    function changePage(index: number): void {
        // Onglet "Filters" indisponible (feature off ou mode avance desactive) : on l'enjambe dans le sens de navigation.
        const target = !filtersTabAvailable && index === FILTERS_TAB
            ? (index > currentTab ? index + 1 : index - 1)
            : index;
        setCurrentTab(target);
        setSearchParams({ tab: target.toString() }, { replace: true });
    };

    function back() {
        changePage(currentTab - 1);
    }

    function next() {
        changePage(currentTab + 1);
    }

    if (pathMetaApiDetail === null) {
        return (<div>Waiting</div>);
    }

    function GetFieldsResume(): FieldResumeDto[] {
        let fieldsResume = new Array<FieldResumeDto>();
        pathMetaApiMappingDtosRef.current.forEach((e) => {
            fieldsResume.push(new FieldResumeDto(
                e.FieldReferenceId,
                GetFieldName(e.FieldReferenceId),
                e.TechnicalName));
        });
        return fieldsResume;
    }

    // Single "simple vs complex" filter state for the page: simple => the per-row FILTER column is
    // the editor and the Filters tab is hidden; complex => the reverse.
    const enterpriseMode = sessionDetail?.EnterpriseMode ?? false;

    return (
        <React.Fragment>
            <div style={{ width: '100%', height: '100%', padding: 24, flexDirection: 'column', justifyContent: 'space-between', alignItems: 'flex-start', display: 'inline-flex' }}>
                <div style={{ alignSelf: 'stretch', height: '100%', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 24, display: 'flex' }}>
                    <div style={{ alignSelf: 'stretch', justifyContent: 'space-between', alignItems: 'center', display: 'inline-flex', minHeight: '49px' }}>
                        {editNameInprogress === null ?
                            <div style={{ justifyContent: 'flex-start', alignItems: 'center', gap: 20, display: 'flex' }}>
                                <div style={{ color: '#E3E3E8', fontSize: 24, fontFamily: 'Manrope', fontWeight: '600', lineHeight: '36px', letterSpacing: 0.48, wordWrap: 'break-word' }}>
                                    {pathMetaApiDetail?.Name ?? "???"}
                                </div>
                                <img src="svgs/edit.svg" alt="circle" style={{ cursor: 'pointer' }} onClick={() => setEditNameInprogress(pathMetaApiDetail?.Name ?? "???")} />
                            </div> :
                            <div style={{ width: '50%', justifyContent: 'flex-start', alignItems: 'center', gap: 10, display: 'flex' }}>
                                <TextBox style={{ width: '50%' }} defaultValue={pathMetaApiDetail?.Name ?? "???"} onValueChange={(e) => setEditNameInprogress(e)} />
                                <Button icon="check" onClick={() => handleNameChange()} />
                                <Button icon="close" onClick={() => setEditNameInprogress(null)} />
                            </div>}
                        <Button text="Close" type="normal" stylingMode="text" onClick={() => cancel()} />
                    </div>
                    <Tabs width='100%' stylingMode='secondary' onSelectedIndexChange={changePage} selectedIndex={currentTab} >
                        <Item text="Source">
                        </Item>
                        <Item text="Exposed field(s)">
                        </Item>
                        <Item text="Filters" visible={filtersTabAvailable} disabled={pathMetaApiDetail.Source instanceof PathDataTableReadDto}>
                        </Item>
                        <Item text="Checking">
                        </Item>
                    </Tabs>
                    {(currentTab === 0) ? <SourceSelection OriginTableId={null} OriginPathId={null} Direction="Source" WithDataTable={true} TableMapping={pathMetaApiDetail.Source ?? null} SetTableMapping={handleTableMappingChange} DataSourceRefreshed={handleDataSourceRefreshed} /> : ""}
                    {(currentTab === 1) ? <MetaApiExposedFields
                        ref={metaApiExposedFieldsRef}
                        MappingChanges={dataChangesRef.current}
                        SetMappingChanges={handleDataChanges}
                        Capabilities={capabilities}
                        TransformationsCapabilities={transformationsCapabilities}
                        Mapping={mapping}
                        IsSimpleFilterMode={enterpriseMode}
                        FiltersEnabled={filterTabsEnabled && sourceHandlesFilters}
                        TableReferenceId={(pathMetaApiDetail.Source as DataSourceDataTableReadDto)?.TableReferenceId ?? 0}
                        AdvancedFilterMode={advancedFilterMode}
                        SetAdvancedFilterMode={handleAdvancedFilterModeChange}
                        AdvancedFilterModeLocked={advancedFilterModeLocked}
                        SelectionIds={selectedIdsRef.current}
                        SetSelectionIds={handleSelectionChanges} /> : ""}
                    {(currentTab === 2 && filtersTabAvailable) ? <FiltersSelection TableMapping={pathMetaApiDetail.Source ?? null} SetTableMapping={handleTableMappingChange} WithSimpleParameters={false} DeclaredParameters={collectDeclaredParameters(pathMetaApiMappingDtosRef.current)} SelectedFieldReferenceIds={selectedIdsRef.current} /> : ""}
                    {(currentTab === 3) ? <MetaApiResume Source={pathMetaApiDetail.Source ?? null} Mapping={GetFieldsResume()} SelectionIds={selectedIdsRef.current ?? []} /> : ""}
                </div>
                <div style={{ width: '100%', height: 64, position: 'absolute', bottom: 32, left: 0, display: 'flex', justifyContent: 'space-between', padding: '16px 24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Button icon='back' text="Back to previous step" type="normal" stylingMode="text" onClick={() => back()} disabled={currentTab === 0} />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                        <PathVersionStatus HasUnsavedChanges={hasUnsavedChanges} IsPublished={isPublished} />
                        <Button text="Save as draft" type="normal" stylingMode="text" onClick={() => save(false)} disabled={!hasUnsavedChanges} />
                        {currentTab === MAX_TAB ?
                            <Button text="Save and publish" type="default" stylingMode="contained" onClick={() => save(true)} disabled={isPublished && !hasUnsavedChanges} /> :
                            <Button text="Continue to next step" type="default" stylingMode="contained" onClick={() => next()} />}
                    </div>
                </div>
            </div>
            <ChangeInProgressForm IsOpen={isOpen} SetIsOpen={setIsOpen} Ok={() => saveDraftFromModal()} Cancel={() => cancelFromModal()} />
            <MessageForm ref={messageFormHandleRef} />
            <Toast
                visible={toastConfig.isVisible}
                message={toastConfig.message}
                type={toastConfig.type}
                onHiding={onHiding}
                displayTime={1000}
            />
        </React.Fragment>
    )
}
